import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const redirectPath = searchParams.get('redirect');
  const purchaseEvent = searchParams.get('purchase');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      const userData = await login(cleanEmail, password);
      
      // Handle redirect with purchase intent
      if (redirectPath) {
        const redirectUrl = purchaseEvent 
          ? `${redirectPath}?purchase=${purchaseEvent}`
          : redirectPath;
        navigate(redirectUrl);
        return;
      }
      
      // Default redirect based on role — only admins get sent to the old
      // admin workspace for backend tasks. Coaches, stream managers,
      // subscribers, and brand-new users all land on the Netflix home at
      // `/` so they stay inside the new experience.
      if (userData.role === 'admin') {
        navigate('/admin');
      } else if (userData.is_stream_manager === true) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      // Distinguish network / server errors from credential errors so users
      // don't get told "check your credentials" when the real problem is a
      // network hiccup or the backend was slow to respond.
      if (err.response) {
        // Server responded with an error (401, 403, 400, 500, etc.)
        const detail = err.response.data?.detail;
        if (err.response.status === 401 || err.response.status === 403) {
          setError(detail || 'Invalid email or password.');
        } else if (err.response.status >= 500) {
          setError('Server error. Please try again in a moment.');
        } else {
          setError(detail || 'Sign in failed. Please try again.');
        }
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('The request timed out. Please check your connection and try again.');
      } else if (err.message === 'Network Error') {
        setError('Network error. Check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
          <img 
            src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
            alt="Elevated Roping Media" 
            className="h-12 w-auto"
          />
          <div className="flex flex-col">
            <span className="font-display font-bold text-sm tracking-tight text-foreground group-hover:text-primary transition-colors leading-none">
              ELEVATED
            </span>
            <span className="font-display font-bold text-xs tracking-wider text-muted-foreground leading-none">
              ROPING MEDIA
            </span>
          </div>
        </Link>
      </div>

      <Card className="sm:mx-auto sm:w-full sm:max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="font-display text-2xl uppercase tracking-wide text-center">
            Welcome Back
          </CardTitle>
          <CardDescription className="text-center">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary font-semibold hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 text-center">
        <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </div>
    </div>
  );
};

export default LoginPage;
